import { useMutation, useQuery } from '@tanstack/react-query';
import http from '../../../utils/http';
import { useShallowEqualEffect } from '../../../utils/use-shallow-effect';
import { Pagination } from '../../@common/crud/types';
import { ServiceTicket } from '../types';
import { Option } from '../../reports/types';

type Params = {
  query?: string;
  status?: string;
  [key: string]: any;
};

export default function useServiceTickets(params: Params = {}) {
  const query = useQuery({
    queryKey: ['api_service_tickets'],
    queryFn: () =>
      http.get<never, Pagination<ServiceTicket>>('/api/service-ticket', {
        params,
      }),
  });

  useShallowEqualEffect(() => {
    void query.refetch();
  }, [params]);

  return query;
}

export function useServiceTicket(id: ServiceTicket['ticketId']) {
  return useQuery({
    queryKey: ['api_service_ticket', id],
    queryFn: () => http.get<never, ServiceTicket>(`/api/service-ticket/${id}`),
  });
}

export function useServiceTicketStatuses() {
  return useQuery({
    queryKey: ['api_service_ticket_statuses'],
    queryFn: () => http.get<never, Option[]>('/api/service-ticket/status'),
  });
}

export function useServiceTicketStatusUpdate(id: ServiceTicket['ticketId']) {
  return useMutation({
    mutationFn: ({ status }: { status: string }) =>
      http.put<never, ServiceTicket>(`/api/service-ticket/${id}/status`, {
        status,
      }),
  });
}

export function useServiceTicketAssignableUsers() {
  return useQuery({
    queryKey: ['api_service_ticket_assignable_users'],
    queryFn: () =>
      http.get<never, Option[]>('/api/service-ticket/assignable-users'),
  });
}

export function useServiceTicketAssignableGroups() {
  return useQuery({
    queryKey: ['api_service_ticket_assignable_groups'],
    queryFn: () =>
      http.get<never, Option[]>('/api/service-ticket/assignable-groups'),
  });
}

export function useServiceTicketAssignedUserUpdate(
  id: ServiceTicket['ticketId'],
) {
  return useMutation({
    mutationFn: ({ userId }: { userId: number | null }) =>
      http.put<never, ServiceTicket>(`/api/service-ticket/${id}/assign-user`, {
        user: userId,
      }),
  });
}

export function useServiceTicketAssignedGroupUpdate(
  id: ServiceTicket['ticketId'],
) {
  return useMutation({
    mutationFn: ({ groupId }: { groupId: number | null }) =>
      http.put<never, ServiceTicket>(`/api/service-ticket/${id}/assign-group`, {
        group: groupId,
      }),
  });
}

export function useServiceTicketDestroy(id: ServiceTicket['ticketId']) {
  return useMutation({
    mutationFn: () =>
      http.delete<never, ServiceTicket>(`/api/service-ticket/${id}`),
  });
}

export function useServiceTicketAddComment(id: ServiceTicket['ticketId']) {
  return useMutation({
    mutationFn: ({ content }: { content: string }) =>
      http.post<never, ServiceTicket>(`/api/service-ticket/${id}/comment`, {
        content,
      }),
  });
}

export function useServiceTicketUpdateComment() {
  return useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) =>
      http.put<never, ServiceTicket>(`/api/service-ticket/comment/${id}`, {
        content,
      }),
  });
}

export function useServiceTicketDestroyComment() {
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      http.delete<never, void>(`/api/service-ticket/comment/${id}`),
  });
}
